import React, { useContext } from "react";
import axios from 'axios';
import { parseISO, formatISO, addMinutes } from "date-fns";
import get from 'lodash.get';
import { useSnackbar } from 'notistack';
import { Box, Alert, Typography, IconButton } from "@mui/material";
import { useTranslation } from 'react-i18next';
import { useForm, useWatch, FormProvider } from 'react-hook-form';
import { Types } from 'utils/constants';
import { renderForMobileContext } from 'utils/app-context';
import { uuidv4 } from "utils/misc";
import useMounted from 'utils/use-mounted';
import { isFieldDirty } from "utils/misc";
import analytics from 'utils/analytics';
import Select from 'components/form-inputs/select';
import useRecordStore from 'contexts/record-store';
import DetailButton from "components/detail-button";
import DrawerContent from "components/menu-drawer/drawer-content";
import DrawerContentBuilder from "components/menu-drawer/drawer-content-builder";
import DrawerActionButton from "components/menu-drawer/drawer-action-button";
import { EditorIconSection } from "components/icon-section/icon-section";
import { LocationEditFields } from "components/location-edit/location-edit";
import { EventEditDateFields } from "../event/edit/event-edit-date";
import ExerciseEditPatrol from "../exercise/edit/exercise-edit-patrol";
import { generateName } from "../exercise/edit/exercise-edit-main";
import AnswerEditWeather from '../answer/edit/answer-edit-weather';
import AnswerEditComments from '../answer/edit/answer-edit-comments';
import AnswerEditPatrol from '../answer/edit/answer-edit-patrol';
import AnswerEditPhotos from '../answer/edit/answer-edit-photos';
import ConfirmDialog from "components/confirm-dialog";
import Icon from "components/icon";
import ConfirmUnsavedEditor from "components/menu-drawer/confirm-unsaved-editor";
import { ReactComponent as QuickTrainingSvg } from 'assets/images/quick-training.svg';
import { DogsContext } from 'contexts/dogs/dogs-context';


const dogHasAllPatrolTypes = (dog, patrolTypeIds) => {
  if (!dog || !patrolTypeIds) {
    return undefined;
  }

  const intersection = dog.patrolAttributes.filter(v => patrolTypeIds.includes(v.id));
  return intersection.length === patrolTypeIds?.length;
};


function QuickTrainingSettingsForm(props) {
  const {
    drawerState,
    options,
    translation,
    formContext,
    date,
    patrolTypeIds,
    confirmEditSettings,
    event,
  } = props;
  const { t } = useTranslation(translation);
  const { control, resetField, setValue, clearErrors } = formContext;
  const { dogs, getActiveDogs } = useContext(DogsContext);
  const futureMinutesBuffer = 5;
  const [maxDateTime, setMaxDateTime] = React.useState(addMinutes(new Date(), futureMinutesBuffer));
  const selectedDogId = useWatch({ control, name: 'soloDogId' });
  // Match hard-coded patrol type select field width
  const fieldWidth = 221;
  const activeDogs = React.useMemo(() => getActiveDogs(date), [getActiveDogs, date]);


  // Reset max time periodically so it stays in the future.
  React.useEffect(() => {
    const interval = setInterval(() => setMaxDateTime(addMinutes(new Date(), futureMinutesBuffer)), 60 * 1000);
    return () => {
      clearInterval(interval);
    };
  }, []);


  // dog auto-(de)selection
  React.useEffect(() => {
    if (activeDogs && patrolTypeIds) {
      if (selectedDogId) {
        // Deselect dog if it is not available on the subsequently selected date
        if (!activeDogs.find(dog => dog.id === selectedDogId)) {
          resetField('soloDogId', { defaultValue: undefined });
        }
      } else if (activeDogs.length > 0) {
        // If no dog was previously selected, auto-select one

        // First look for a default dog
        let dogToSelect = activeDogs.find(dog => dog.isDefault);

        if (!dogToSelect) {
          // If there isn't a default, choose the first one that is compatible with the 
          // selected patrol type(s).
          dogToSelect = activeDogs.find(dog => dogHasAllPatrolTypes(dog, patrolTypeIds));
        }

        if (!dogToSelect) {
          // If we still don't have a candidate, just choose the first one.
          dogToSelect = activeDogs[0];
        }

        resetField('soloDogId', { defaultValue: dogToSelect.id });
        clearErrors('soloDogId');
      }
    }
  }, [selectedDogId, activeDogs, resetField, clearErrors, patrolTypeIds]);


  // generate name from patrol types
  React.useEffect(() => {
    const newName = generateName(Types.WorkMode.PATROL, patrolTypeIds)
    setValue('exercises[0].name', newName)
  }, [patrolTypeIds, setValue]);


  const selectedDogIsIncompatible = React.useMemo(() => {
    if (selectedDogId && dogs && patrolTypeIds?.length > 0) {
      const selectedDog = dogs.find(dog => dog.id === selectedDogId)
      return !dogHasAllPatrolTypes(selectedDog, patrolTypeIds);
    }
  }, [dogs, patrolTypeIds, selectedDogId]);


  // TODO: spacing between field containers is tweaked to make them appear vertically 
  //  equidistant, compensating for the spacing set by the subcomponents. A more 
  //  robust solution would be to move spacing logic to parent components everywhere.


  return (
    <EditorIconSection
      iconName='settings'
      helpProps={{
        translation,
        section: 'settings'
      }}
    >
      {/* form control styling simulates what's set in HelpTextitem, which isn't used
          here because the UI is mobile-only */}
      <Box sx={{ '& .MuiFormControl-root': { width: '100%', mt: 0.5 } }}>
        <LocationEditFields
          locationOptions={options.locations}
          countryOptions={options.countries}
          drawerState={drawerState}
          recordType={Types.Record.EVENT}
          doAutofill={renderForMobileContext()}
        />

        <Box sx={{ mt: 3 }}>
          <EventEditDateFields
            options={options}
            storedRecord={event}
            translation='eventEdit'
            forceNowDefault
            stackedFieldWidth={fieldWidth}
            rowSpacing={3}
            maxDate={maxDateTime}
            maxDateMessage={t('dateTime.errMsgMaxDate')}
          />
        </Box>

        <Box sx={{ mt: 3 }}>
          <Box sx={{ width: fieldWidth }}>
            <Select
              name={'soloDogId'}
              label={t('dog.label')}
              options={activeDogs}
              rules={{ required: t(activeDogs.length === 0 ? 'dog.errMsgUnavailable' : 'dog.errMsgEmpty') }}
              disabled={activeDogs.length === 0}
              confirmOpen={confirmEditSettings}
            />
          </Box>
          {selectedDogIsIncompatible &&
            <Alert severity='warning' sx={{ mt: 1 }}>
              <Typography>
                The selected dog is not configured to complete this type of exercise.
                If this is unexpected then you may need to edit the dog and select
                appropriate <strong>Patrol</strong> types.
              </Typography>
            </Alert>
          }
        </Box>

        <Box sx={{ mt: 2.5 }}>
          <ExerciseEditPatrol
            drawerState={drawerState}
            storedRecord={event.exercises[0]}
            workModeValue={Types.WorkMode.PATROL}
            formPrefix='exercises[0].'
            options={options}
            confirmChange={confirmEditSettings}
            compact
          />
        </Box>
      </Box>
    </EditorIconSection>
  );
}


function QuickTrainingAnswerForm(props) {
  const {
    patrol,
    formContext,
    options,
    drawerState,
    location,
    date,
  } = props;
  const { getValues } = formContext;
  const { t: gt } = useTranslation('general');
  const containerRef = React.useRef();
  const workMode = Types.WorkMode.PATROL;
  const dateString = formatISO(date);
  // memoizing in case being a new object on every render causes problems, 
  // i.e. if storedRecord is used as a dependency
  const simStoredRecord = React.useMemo(() => ({ 
    patrol: patrol, 
    eventDate: dateString 
  }), [dateString, patrol]);
  const formProps = {
    storedRecord: simStoredRecord,
    translation: 'answerEdit',
    formPrefix: 'exercises[0].answers[0].',
    options,
    drawerState,
    date,
    location,
    workMode: workMode,
    workModeName: Types.WorkMode.getName(gt, workMode).toLowerCase(),
  };


  React.useEffect(() => {
    containerRef.current.scrollIntoView({ behavior: 'smooth' });
  }, []);

  
  return (
    <Box sx={{ pt: 3 }} ref={containerRef}>

      {/* patrol */}
      <AnswerEditPatrol {...formProps} defaultMonitor={getValues('exercises[0].monitor')} />

      {/* weather */}
      <AnswerEditWeather {...formProps} />

      {/* comments */}
      <AnswerEditComments {...formProps} />

      {/* photos */}
      <AnswerEditPhotos {...formProps} />

      <Alert severity='info'>
        <Typography>
          You're encouraged to review your training record later to ensure the
          auto-created event and exercise include appropriate details about
          your training.
        </Typography>
      </Alert>
    </Box>
  );
}


export default function QuickTrainingForm(props) {
  const { drawerState, onClose } = props;
  const translation = 'quickTraining';
  const isMounted = useMounted();
  const formContext = useForm();
  const { enqueueSnackbar } = useSnackbar();
  const recordStore = useRecordStore();
  const { 
    control, 
    formState, 
    handleSubmit, 
    reset, 
    setValue, 
    trigger, 
    unregister,
  } = formContext;
  const [options, setOptions] = React.useState();
  const [event, setEvent] = React.useState();
  const [isCompleting, setIsCompleting] = React.useState(false);
  const [confirmEditSettingsFromDialog, setConfirmEditSettingsFromDialog] = React.useState();
  const [submittedState, setSubmittedState] = React.useState();
  const patrol = useWatch({ control, name: 'exercises[0].patrol' });
  const patrolTypeIds = useWatch({ control, name: 'patrolTypeIds' });
  const location = useWatch({ control, name: 'location' });
  // (from event-date-edit)
  const dateValue = useWatch({ control, name: 'date' });
  const dateObj = typeof dateValue === 'string' ? parseISO(dateValue) : dateValue;


  // get event/exercise init and options
  React.useEffect(() => {
    // weird hot reloading behaviour without this...
    cancelCompletion();

    drawerState.loading.content.set(true);
    (async () => {
      const response = await axios.get(`/training/event/quick`);
      if (!isMounted.current) return;

      const data = response.data;
      // Normally we'd be adding the event/exercise to the record-store at this point, but we don't want 
      // any trace left if the process isn't followed through to save, so we'll pass local state objects
      // to components that expect a "storedRecord".
      const trainingEvent = data.event

      // As serialized by the server, patrolType.buildingSearch and patrolType.humanSearch will be null
      // if irrelevant. Anything other than undefined will prevent nested form default values from being
      // applied, so replace null with undefined. (Default values, especially arrays, are more important here
      // than elsewhere because we're allowing them to be hidden and submitted without modification.)
      const patrolType = trainingEvent.exercises[0].patrol.patrolTypes[0];
      if (patrolType.buildingSearch === null) {
        delete patrolType.buildingSearch;
      }
      if (patrolType.humanSearch === null) {
        delete patrolType.humanSearch;
      }

      // Give the patrol type as a local unique key before persistence
      patrolType.uuid = uuidv4();

      // Include existing patrolTypeIds when resetting because remounting/reinitializing this component doesn't
      // guarantee that the ExerciseEditPatrol subcomponent will remount/reinitialize. In that case, patrolTypeIds
      // will be left at whatever value they are reset to here. (Observed in development with hot reload when code in this 
      // component changes.)
      reset({ ...trainingEvent, patrolTypeIds });
      setEvent(trainingEvent);
      setOptions(data.options);

      drawerState.loading.content.set(false);
    })();

  // init once after initial render
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  // Ensure that ConfirmUnsavedEditor props are up to date by triggering 
  // a re-render before proceding with save and navigation.
  // (onSubmit > useEffect > afterSubmit)
  const onSubmit = React.useCallback((data) => {
    setSubmittedState({ data });
  }, []);


  React.useEffect(() => {
    if (submittedState) {
      afterSubmit(submittedState.data);
    }

  // only call afterSubmit when submittedState changes, not when afterSubmit itself changes
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [submittedState]);


  const afterSubmit = React.useCallback(async (data) => {
    drawerState.loading.content.set(true);

    try {
      const { record } = await recordStore.saveQuickTrainingEvent(data);
      if (record) {
        // track which predicted fields were changed
        let dirtyPredictedFields = [];
        if (isFieldDirty(get(formState.dirtyFields, 'location'))) dirtyPredictedFields.push('Location');
        if (isFieldDirty(get(formState.dirtyFields, 'date'))) dirtyPredictedFields.push('DateTime');
        if (isFieldDirty(get(formState.dirtyFields, 'duration'))) dirtyPredictedFields.push('Duration');
        if (isFieldDirty(get(formState.dirtyFields, 'soloDogId'))) dirtyPredictedFields.push('Dog');
        if (isFieldDirty(get(formState.dirtyFields, 'exercises[0].patrol.patrolTypes[0].type'))) dirtyPredictedFields.push('PatrolType');
        if (data.exercises[0].patrol.patrolTypes.length !== 1) dirtyPredictedFields.push('isScenario');
        const modifiedFields = dirtyPredictedFields.length > 0 ? dirtyPredictedFields.join(',') : 'None';
        analytics.trackGA4('event', {
          name: 'quick_save',
          params: {
            complete_type: isCompleting ? 'now' : 'later',
            modified_fields: modifiedFields
          }
        });

        enqueueSnackbar(`Quick training saved${isCompleting ? ' and completed' : ' for later'}`, {
          variant: 'success'
        });

        onClose();
      } else {
        drawerState.loading.content.set(false);
      }
    } catch (e) {
      enqueueSnackbar('Error saving quick training', {
        variant: 'error',
        persist: true,
        anchorOrigin: {
          vertical: "top",
          horizontal: "center"
        }
      });

      drawerState.loading.content.set(false);
    }


  // recordStore is a hookstate
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drawerState.loading.content, enqueueSnackbar, isCompleting, onClose]);


  // initialize completion
  const handleCompleteClick = async () => {
    // validate first
    if (await trigger()) {
      // copy patrol from exercise to answer and set defaults
      setValue('exercises[0].answers[0]', { patrol, wasExercisePerformed: true, comments: '' });

      setIsCompleting(true);
    }
  };


  const cancelCompletion = React.useCallback(() => {
    setIsCompleting(false);
    unregister('exercises[0].answers[0]')
  }, [unregister]);


  const confirmEditSettings = async () => {
    if (isCompleting) {
      const shouldChange = await confirmEditSettingsFromDialog();
      if (shouldChange) {
        cancelCompletion();
      }
      return shouldChange;
    } else {
      return true;
    }
  };


  const onSave = React.useCallback(() => {
    handleSubmit(onSubmit)();
  }, [handleSubmit, onSubmit]);


  const saveButton = React.useMemo(() => (
    <DrawerActionButton
      style={{ marginLeft: 16 }}
      onClick={onSave}
    >
      Save
    </DrawerActionButton>
  ), [onSave]);


  const closeButton = React.useMemo(() => (
    <IconButton
      size="small"
      style={{ color: 'inherit' }}
      onClick={onClose}
    >
      <Icon type='close' />
    </IconButton>
  ), [onClose]);


  const cancelButton = React.useMemo(() => (
    <DrawerActionButton
      style={{ marginRight: 16 }}
      onClick={() => cancelCompletion()}
    >
      Cancel
    </DrawerActionButton>
  ), [cancelCompletion]);


  return (
    <>
      <FormProvider {...formContext}>
        <DrawerContentBuilder
          mobileAppBar={{
            title: 'Solo Quick Training',
            leftActions: isCompleting ? cancelButton : closeButton,
            rightActions: isCompleting ? saveButton : undefined,
          }}
        >
          {(event && options)
            ? <>
              <form onSubmit={handleSubmit(onSubmit)}>
                <DrawerContent>
                  <Box sx={{ mb: 4 }}>
                    Use Solo Quick Training when you're training alone on a patrol exercise
                    with one dog at the current location. The training event and exercise
                    record are created automatically.
                  </Box>

                  <QuickTrainingSettingsForm
                    {...{
                      drawerState,
                      options,
                      translation,
                      formContext,
                      patrolTypeIds,
                      confirmEditSettings,
                      event,
                    }}
                    date={dateObj}
                  />

                  {isCompleting &&
                    <QuickTrainingAnswerForm
                    {...{
                      drawerState, 
                      options, 
                      formContext, 
                      patrol, 
                      location, 
                    }}
                      date={dateObj}
                    />
                  }

                  {!isCompleting &&
                    <Box sx={{ mt: 5 }}>
                      <DetailButton
                        iconId='checkmark-outline'
                        title='Complete Exercise'
                        description='Complete this exercise now by adding your comments and other details.'
                        color='info'
                        variant='contained'
                        onClick={handleCompleteClick}
                      />
                      <DetailButton
                        iconId='save'
                        title='Save for Later'
                        description='Save this exercise and provide the dog completion details at a later time.'
                        color='info'
                        variant='contained'
                        onClick={onSave}
                      />
                    </Box>
                  }
                </DrawerContent>
              </form>

              <ConfirmDialog
                titleStyle="warning"
                title='Confirm Settings Change'
                titleIcon='warning'
                content={<>
                  If you change the dog or patrol type settings, the completion will be removed and
                  completion information will be lost. Press OK to proceed.
                </>}
                setConfirm={setConfirmEditSettingsFromDialog}
              />

              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  mt: 8,
                  '& svg': {
                    height: 130,
                    width: 'auto',
                    opacity: 0.4,
                  }
                }}
              >
                <QuickTrainingSvg />
              </Box>
            </>
            : null
          }
        </DrawerContentBuilder>
      </FormProvider>

      <ConfirmUnsavedEditor
        dirty={formState.isDirty && !formState.isSubmitted}
        save={async () => {
          return await new Promise((resolve) => {
            handleSubmit(
              async (data) => {
                onSubmit(data, true);
                return resolve(true);
              },
              () => {
                return resolve(false);
              })();
          });
        }}
      />
    </>
  );
}